import React, { useCallback, useEffect, useState } from "react";
import { Check, Edit3, Landmark, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { ActionIcon } from "../components/ActionIcon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function AdminCarriers() {
  const [carriers, setCarriers] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [assignment, setAssignment] = useState({ company: "", covered_from: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/carriers");
    setCarriers(data);
  }, []);

  const loadEmployers = useCallback(async () => {
    const { data } = await api.get("/admin/companies");
    setEmployers(data.filter((c) => c.registered).map((c) => c.name));
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    loadEmployers().catch(() => setEmployers([]));
  }, [load, loadEmployers]);

  const activeCarrier = carriers.find((c) => c.id === active) || null;

  const add = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/carriers", { name: newName });
      toast.success(`${newName} added`);
      setNewName("");
      setActive(data.id);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const rename = async (carrier) => {
    try {
      await api.put(`/admin/carriers/${carrier.id}`, { name: editedName });
      toast.success("Carrier renamed");
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (carrier) => {
    if (!window.confirm(`Delete ${carrier.name} and its ${carrier.employers.length} employer assignment(s)?`)) return;
    try {
      await api.delete(`/admin/carriers/${carrier.id}`);
      toast.success("Carrier deleted");
      if (active === carrier.id) setActive("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const assign = async (event) => {
    event.preventDefault();
    if (!assignment.company || !assignment.covered_from) {
      return toast.error("Choose an employer and a covered-from date");
    }
    try {
      await api.post(`/admin/carriers/${active}/employers`, assignment);
      toast.success(`${assignment.company} mapped to ${activeCarrier.name}`);
      setAssignment({ company: "", covered_from: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unassign = async (link) => {
    try {
      await api.delete(`/admin/carriers/${active}/employers/${link.id}`);
      toast.success("Assignment removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10" data-testid="admin-carriers-page">
      <div className="mb-8">
        <div className="eyebrow mb-2">Administration</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>
          Carriers
        </h1>
        <p className="mt-3 text-base max-w-2xl" style={{ color: "var(--muted)" }}>
          Insurance carriers and the employers they cover. An employer can sit with several carriers
          over time — the covered-from date records when each relationship starts.
        </p>
      </div>

      <form onSubmit={add} className="border p-6 mb-8 flex flex-col sm:flex-row gap-4 sm:items-end" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="add-carrier-form">
        <div className="flex-1">
          <label className="field-label">New carrier</label>
          <input className="input-flat" required minLength={2} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Prudential" data-testid="new-carrier-name"/>
        </div>
        <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2" data-testid="add-carrier-submit">
          <Plus size={14}/> {saving ? "Saving…" : "Add Carrier"}
        </button>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border overflow-x-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <table className="w-full text-sm min-w-[560px]" data-testid="carriers-table">
            <thead>
              <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
                <th className="px-4 py-3">Carrier</th>
                <th className="px-4 py-3">Employers covered</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {carriers.map((carrier) => (
                <tr key={carrier.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`carrier-row-${carrier.id}`}>
                  <td className="px-4 py-3 font-medium" style={{ color: "var(--ink)" }}>
                    {editingId === carrier.id ? (
                      <input className="input-flat" value={editedName} autoFocus onChange={(e) => setEditedName(e.target.value)} data-testid={`edit-carrier-input-${carrier.id}`}/>
                    ) : (
                      <span className="flex items-center gap-2"><Landmark size={14} style={{ color: "var(--muted)" }}/> {carrier.name}</span>
                    )}
                  </td>
                  <td className="px-4 py-3" style={{ color: "var(--muted)" }}>
                    {carrier.employers.length ? (
                      <span className="flex flex-wrap gap-1.5">
                        {carrier.employers.map((link) => (
                          <span key={link.id} className="pill text-[10px]">{link.company} · from {link.covered_from}</span>
                        ))}
                      </span>
                    ) : "None yet"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1 justify-end">
                      {editingId === carrier.id ? (
                        <>
                          <ActionIcon icon={Check} label="Save name" onClick={() => rename(carrier)} testId={`save-carrier-${carrier.id}`}/>
                          <ActionIcon icon={X} label="Cancel" onClick={() => setEditingId(null)} testId={`cancel-carrier-${carrier.id}`}/>
                        </>
                      ) : (
                        <>
                          <button onClick={() => setActive(carrier.id)} className="btn-ghost !py-1.5 !px-3 text-xs mr-1" data-testid={`manage-carrier-${carrier.id}`}>Manage</button>
                          <ActionIcon icon={Edit3} label="Rename carrier" onClick={() => { setEditingId(carrier.id); setEditedName(carrier.name); }} testId={`edit-carrier-${carrier.id}`}/>
                        </>
                      )}
                      <ActionIcon icon={Trash2} label="Delete carrier" onClick={() => remove(carrier)} testId={`delete-carrier-${carrier.id}`}/>
                    </span>
                  </td>
                </tr>
              ))}
              {!carriers.length && (
                <tr><td colSpan={3} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="carriers-empty">
                  No carriers yet — add the first one above.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="border" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="carrier-detail">
          <div className="p-5 border-b" style={{ borderColor: "var(--border)" }}>
            <div className="eyebrow">Covered employers</div>
            <div className="font-heading font-bold text-xl mt-1" style={{ color: "var(--ink)" }}>
              {activeCarrier?.name || "Select a carrier"}
            </div>
          </div>
          {activeCarrier && (
            <>
              <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                {activeCarrier.employers.map((link) => (
                  <li key={link.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`carrier-employer-${link.id}`}>
                    <span>
                      <span className="text-sm font-medium" style={{ color: "var(--ink)" }}>{link.company}</span>
                      <span className="block text-xs" style={{ color: "var(--muted)" }}>Covered from {link.covered_from}</span>
                    </span>
                    <ActionIcon icon={Trash2} label="Remove this assignment" onClick={() => unassign(link)} testId={`delete-carrier-employer-${link.id}`}/>
                  </li>
                ))}
                {!activeCarrier.employers.length && (
                  <li className="px-5 py-6 text-sm" style={{ color: "var(--muted)" }}>No employers mapped yet.</li>
                )}
              </ul>
              <form onSubmit={assign} className="p-5 border-t space-y-4" style={{ borderColor: "var(--border)" }} data-testid="assign-carrier-employer-form">
                <div>
                  <label className="field-label">Employer</label>
                  <Select value={assignment.company} onValueChange={(v) => setAssignment({ ...assignment, company: v })}>
                    <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="carrier-employer-select">
                      <SelectValue placeholder="Choose An Employer"/>
                    </SelectTrigger>
                    <SelectContent className="!rounded-none max-h-72">
                      {employers.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="field-label">Covered from</label>
                  <input className="input-flat" type="date" value={assignment.covered_from} onChange={(e) => setAssignment({ ...assignment, covered_from: e.target.value })} data-testid="carrier-covered-from"/>
                </div>
                <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" data-testid="assign-carrier-employer-submit">
                  <Plus size={14}/> Assign Employer
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
